import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/leave/accrual-logs")({
  component: AccrualLogsPage,
});

import { useState, useEffect, useMemo } from "react";
import { useDebounce } from "@/hooks/use-debounce";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Input } from "@/components/ui/input";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";

import useFetchAccrualLogs, {
  type AccrualLogOrdering,
} from "@/hooks/api/leave/accrual-logs/useFetchAccrualLogs";
import useFetchLeaves from "@/hooks/api/leave/leave/useFetchLeaves";

import { AccrualLogsTable } from "@/components/crud/table/AccrualLogsTable";

import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";

const MONTHS = [
  { label: "All months", value: "clear" },
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

function AccrualLogsPage() {
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        period_year: SortDirection;
        period_month: SortDirection;
        created_at: SortDirection;
      },
      AccrualLogOrdering
    >({
      period_year: null,
      period_month: null,
      created_at: null,
    });

  const [yearFilter, setYearFilter] = useState<string>("");
  const [debouncedYear] = useDebounce(yearFilter, 500);
  const [monthFilter, setMonthFilter] = useState<string | undefined>();
  const [leaveFilter, setLeaveFilter] = useState<string | undefined>();
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);

  const accrualLogsQuery = useFetchAccrualLogs({
    page_size: pageSize,
    page,
    ordering: getApiOrdering(),
    period_year: debouncedYear ? Number(debouncedYear) : undefined,
    period_month:
      monthFilter && monthFilter !== "clear" ? Number(monthFilter) : undefined,
    leave: leaveFilter ? Number(leaveFilter) : undefined,
  });
  const accrualLogs = accrualLogsQuery.data?.results ?? [];
  const count = accrualLogsQuery.data?.count ?? 0;

  const leavesQuery = useFetchLeaves({ page_size: 100 });
  const leaves = leavesQuery.data?.results ?? [];

  const isLoading = accrualLogsQuery.isPending;
  const isFetching = accrualLogsQuery.isFetching;

  const resetFilters = () => {
    setOrdering({ period_year: null, period_month: null, created_at: null });
    setYearFilter("");
    setMonthFilter(undefined);
    setLeaveFilter(undefined);
    setPage(1);
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedYear, monthFilter, leaveFilter, ordering, setPage]);

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const leaveOptions = [
    { label: "All", value: "clear" },
    ...leaves.map((l) => ({ label: l.name, value: String(l.id) })),
  ];

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Accrual Logs"
          description="Read-only audit trail of all leave days automatically granted by accrual rules."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="flex flex-col gap-1">
            <Input
              type="number"
              placeholder="Year"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="w-32"
              min={2000}
              max={2100}
            />
          </div>

          <SelectFilter
            placeholder="Month"
            options={MONTHS}
            value={monthFilter}
            onChange={(v) => setMonthFilter(v)}
          />

          <SelectFilter
            placeholder="Leave Type"
            options={leaveOptions}
            value={leaveFilter}
            onChange={(v) => setLeaveFilter(v === "clear" ? undefined : v)}
          />
        </FiltersPanel>

        <AccrualLogsTable
          accrualLogs={accrualLogs}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
    </SidebarInset>
  );
}
